// ============================================================================
// sensitiveDataPolicy.js — Sensitive Data Classification + Encryption Boundary
// CLIENT policy (ESM). Pure: no Firebase / network / logging.
//
// Canonical CLIENT mirror of `functions/sensitiveDataPolicy.js`. The two modules
// are kept identical (categories, forbidden field names, safe operational
// allowlist) and `src/core/sensitiveDataPolicy.parity.verify.js` blocks drift.
//
// See the server module header for the full design notes. Key points:
//   - Field-name matching is EXACT (normalized), never substring — so safe names
//     that merely contain a risky word (patientContextPresent, receiptToken,
//     latestEhrImportJobId) are never false-positives.
//   - Callers that guard the referral root treat `answers`/free-text subtrees as
//     OPAQUE; their PHI safety is governed by pack authoring + `phiScan.js`.
//   - `phi_access_logs` is a PHI-plane audit collection and may record reference
//     ids (patientId/fhirResourceId) via { allowReferenceIds:true }.
// ============================================================================

// ── Categories ────────────────────────────────────────────────────────────────
export const SENSITIVE_CATEGORY = Object.freeze({
  PHI: 'PHI',
  PHI_ADJACENT: 'PHI_ADJACENT',
  TOKEN: 'TOKEN',
  SECRET: 'SECRET',
  RAW_FHIR_PAYLOAD: 'RAW_FHIR_PAYLOAD',
  ATTACHMENT_CONTENT: 'ATTACHMENT_CONTENT',
  ATTACHMENT_METADATA: 'ATTACHMENT_METADATA',
  FLOW_SECRET: 'FLOW_SECRET',
  ENCRYPTED_TRANSIENT: 'ENCRYPTED_TRANSIENT',
  SAFE_OPERATIONAL_METADATA: 'SAFE_OPERATIONAL_METADATA',
  SAFE_COUNT_OR_STATUS: 'SAFE_COUNT_OR_STATUS',
  REDACTED_LOG_METADATA: 'REDACTED_LOG_METADATA',
});

export const VIOLATION_CODE = 'SENSITIVE_DATA_VIOLATION';

// ── Forbidden field names (normalized) → category ─────────────────────────────
// EXACT-name denylist for Never-PHI zones. Keep byte-identical with the server mirror.
export const FORBIDDEN_FIELD_CATEGORY = Object.freeze({
  // PHI — identity
  patientname: 'PHI', patientfirstname: 'PHI', patientlastname: 'PHI',
  patientfullname: 'PHI', firstname: 'PHI', lastname: 'PHI', fullname: 'PHI',
  givenname: 'PHI', familyname: 'PHI',
  dob: 'PHI', dateofbirth: 'PHI', birthdate: 'PHI',
  ssn: 'PHI', socialsecuritynumber: 'PHI',
  // PHI — contact
  phone: 'PHI', phonenumber: 'PHI', telephone: 'PHI', mobilephone: 'PHI',
  email: 'PHI', emailaddress: 'PHI',
  address: 'PHI', streetaddress: 'PHI', homeaddress: 'PHI', mailingaddress: 'PHI',
  zipcode: 'PHI', postalcode: 'PHI',
  // PHI — insurance / MRN
  mrn: 'PHI', medicalrecordnumber: 'PHI',
  memberid: 'PHI', subscriberid: 'PHI', insurancememberid: 'PHI',
  policynumber: 'PHI', groupnumber: 'PHI',
  // PHI-adjacent — external / EHR / FHIR identifiers
  ehrpatientid: 'PHI_ADJACENT', fhirpatientid: 'PHI_ADJACENT',
  patientid: 'PHI_ADJACENT', patientreference: 'PHI_ADJACENT',
  fhirresourceid: 'PHI_ADJACENT', fhiruser: 'PHI_ADJACENT', fhiruserhash: 'PHI_ADJACENT',
  storagepath: 'PHI_ADJACENT',
  // TOKEN
  accesstoken: 'TOKEN', refreshtoken: 'TOKEN', idtoken: 'TOKEN',
  bearertoken: 'TOKEN', authtoken: 'TOKEN',
  authorizationcode: 'TOKEN', authcode: 'TOKEN', codeverifier: 'TOKEN',
  // SECRET
  clientsecret: 'SECRET', secret: 'SECRET', password: 'SECRET',
  privatekey: 'SECRET', apikey: 'SECRET',
  encryptedpatientcontext: 'SECRET', encryptedaccesstoken: 'SECRET',
  // RAW FHIR
  rawfhir: 'RAW_FHIR_PAYLOAD', fhirresponse: 'RAW_FHIR_PAYLOAD',
  fhirresource: 'RAW_FHIR_PAYLOAD', fhirbundle: 'RAW_FHIR_PAYLOAD',
  fhirpayload: 'RAW_FHIR_PAYLOAD',
  // ATTACHMENT content / metadata
  attachmenttext: 'ATTACHMENT_CONTENT', documenttext: 'ATTACHMENT_CONTENT',
  ocrtext: 'ATTACHMENT_CONTENT', rawtext: 'ATTACHMENT_CONTENT',
  originalname: 'ATTACHMENT_METADATA', filename: 'ATTACHMENT_METADATA',
});

export const FORBIDDEN_NEVER_PHI_FIELDS = Object.freeze(Object.keys(FORBIDDEN_FIELD_CATEGORY).sort());

// Reference ids a PHI-plane access log may legitimately record (see header).
export const ACCESS_LOG_ALLOWED_REFERENCE_IDS = Object.freeze(['patientid', 'fhirresourceid', 'patientreference']);

// Always-safe operational names (defensive allowlist; documents intent).
export const SAFE_OPERATIONAL_FIELDS = Object.freeze([
  'attachmentid', 'candidatecount', 'candidateid', 'code', 'connectionid',
  'coveragecandidatecount', 'coverageimported', 'documentcount', 'documentid',
  'ehrconnectionid', 'ehrconnectionstatus', 'ehrimportcandidatecount',
  'ehrimportstatus', 'ehrlaunchsessionid', 'ehrsourcesystemlabel',
  'encountercontextpresent', 'errorcode', 'eventid', 'fhirusercontextpresent',
  'importjobid', 'jobid', 'latestehrimportjobid', 'launchsessionid', 'packetid',
  'patientcandidatecount', 'patientcontextpresent', 'patientimported',
  'readydocumentcount', 'receipttoken', 'referralid', 'runid', 'schemaversion',
  'sourcesystem', 'sourcesystemlabel', 'sourcetype', 'status', 'tenantid',
]);

// ── Value heuristics ──────────────────────────────────────────────────────────
const EMAIL_VALUE = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/;
const JWT_VALUE = /\beyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]+/;
const BEARER_VALUE = /\bBearer\s+[A-Za-z0-9._~+/-]{12,}=*/i;
const OAUTH_TOKEN_KV = /(access_token|refresh_token|id_token)\s*[=:]\s*[A-Za-z0-9._-]{8,}/i;
const PHONE_VALUES = [
  /\b\d{3}[-.]?\d{3}[-.]?\d{4}\b/,
  /\b\(\d{3}\)\s*\d{3}[-.]?\d{4}\b/,
  /\b\+\d{1,3}\s?\(\d{3}\)\s*\d{3}[-.]?\d{4}\b/,
];
const LONG_NUMBER_VALUE = /\b\d{9,}\b/;
// S4 — URL query strings carry OAuth code/state/launch/token params; drop wholesale.
const URL_QUERY = /(\bhttps?:\/\/[^\s?#'"<>]+)\?[^\s#'"<>]*/gi;
// S4 — explicit secret/credential key=value pairs (broader than OAUTH_TOKEN_KV).
const SENSITIVE_KV = /\b(code_verifier|client_secret|authorization_code|auth_code|access_token|refresh_token|id_token|assertion|password|passwd|pwd|api[_-]?key|secret)\b\s*[=:]\s*['"]?[^\s'"]{4,}/gi;

export function normName(name) {
  return String(name == null ? '' : name).toLowerCase().replace(/[^a-z0-9]/g, '');
}

function isPlainObject(v) {
  return v !== null && typeof v === 'object' && !Array.isArray(v)
    && (v.constructor === Object || v.constructor === undefined);
}

/** Classify a string VALUE as TOKEN/PHI if high-confidence sensitive, else null. */
export function classifyValue(s) {
  if (typeof s !== 'string' || s.length === 0) return null;
  if (JWT_VALUE.test(s) || BEARER_VALUE.test(s) || OAUTH_TOKEN_KV.test(s)) return SENSITIVE_CATEGORY.TOKEN;
  if (EMAIL_VALUE.test(s)) return SENSITIVE_CATEGORY.PHI;
  return null;
}

/** @returns {string|null} category if the field name is forbidden in Never-PHI zones. */
export function classifyFieldName(name) {
  const nk = normName(name);
  if (SAFE_OPERATIONAL_FIELDS.indexOf(nk) !== -1) return null;
  return Object.prototype.hasOwnProperty.call(FORBIDDEN_FIELD_CATEGORY, nk)
    ? FORBIDDEN_FIELD_CATEGORY[nk] : null;
}

export function isForbiddenNeverPhiFieldName(name) {
  return classifyFieldName(name) !== null;
}

// ── Typed violation ───────────────────────────────────────────────────────────
export class SensitiveDataViolation extends Error {
  constructor({ field, category, context } = {}) {
    super(`${VIOLATION_CODE}: field "${field}" classified ${category} is not allowed in ${context || 'a Never-PHI zone'}`);
    this.name = 'SensitiveDataViolation';
    this.code = VIOLATION_CODE;      // typed code for callers / UI fail-fast mapping
    this.field = field || null;      // SAFE: field NAME only — never the value
    this.category = category || null;
    this.context = context || null;
  }
}

// ── Never-PHI document validator (hard-fail) ──────────────────────────────────
/**
 * Throw SensitiveDataViolation if `docObj` carries any forbidden field name or a
 * token-/email-shaped VALUE. Pure; never includes a sensitive VALUE in the error.
 *
 * @param {object} docObj
 * @param {{ context?: string, opaqueKeys?: string[], allowReferenceIds?: boolean }} [options]
 */
export function assertSafeNeverPhiDoc(docObj, options) {
  const opts = options || {};
  const context = opts.context || 'a Never-PHI zone';
  const opaque = {};
  (opts.opaqueKeys || []).forEach((k) => { opaque[normName(k)] = true; });
  walk(docObj, context, opaque, !!opts.allowReferenceIds, 0);
  return true;
}

function walk(node, context, opaque, allowRefIds, depth) {
  if (depth > 8) return;
  if (Array.isArray(node)) {
    for (const item of node) {
      if (typeof item === 'string') {
        const c = classifyValue(item);
        if (c) throw new SensitiveDataViolation({ field: '(array item)', category: c, context });
      } else if (Array.isArray(item) || isPlainObject(item)) {
        walk(item, context, opaque, allowRefIds, depth + 1);
      }
    }
    return;
  }
  if (!isPlainObject(node)) return;
  for (const key of Object.keys(node)) {
    const nk = normName(key);
    const cat = classifyFieldName(key);
    if (cat && !(allowRefIds && ACCESS_LOG_ALLOWED_REFERENCE_IDS.indexOf(nk) !== -1)) {
      throw new SensitiveDataViolation({ field: key, category: cat, context });
    }
    if (opaque[nk]) continue; // clinical/free-text subtree — out of scope for the structural guard
    const val = node[key];
    if (typeof val === 'string') {
      const c = classifyValue(val);
      if (c) throw new SensitiveDataViolation({ field: key, category: c, context });
    } else if (Array.isArray(val) || isPlainObject(val)) {
      walk(val, context, opaque, allowRefIds, depth + 1);
    }
  }
}

/** Access-log `extra` guard: rejects PHI/token/secret values but permits audit refs. */
export function assertSafeAccessLogExtra(extra, context) {
  if (extra == null) return true;
  if (!isPlainObject(extra)) {
    throw new SensitiveDataViolation({ field: '(extra)', category: SENSITIVE_CATEGORY.PHI_ADJACENT, context: context || 'phi_access_logs.extra' });
  }
  return assertSafeNeverPhiDoc(extra, { context: context || 'phi_access_logs.extra', allowReferenceIds: true });
}

/** Throw if any TOKEN/SECRET field name or token-shaped value is present. */
export function assertNoTokenOrSecret(obj, context) {
  const ctx = context || 'token/secret boundary';
  walkTokenSecret(obj, ctx, 0);
  return true;
}

function walkTokenSecret(node, context, depth) {
  if (depth > 8) return;
  if (Array.isArray(node)) {
    for (const item of node) {
      if (typeof item === 'string') {
        if (classifyValue(item) === SENSITIVE_CATEGORY.TOKEN) throw new SensitiveDataViolation({ field: '(array item)', category: SENSITIVE_CATEGORY.TOKEN, context });
      } else if (Array.isArray(item) || isPlainObject(item)) walkTokenSecret(item, context, depth + 1);
    }
    return;
  }
  if (!isPlainObject(node)) return;
  for (const key of Object.keys(node)) {
    const cat = classifyFieldName(key);
    if (cat === SENSITIVE_CATEGORY.TOKEN || cat === SENSITIVE_CATEGORY.SECRET) {
      throw new SensitiveDataViolation({ field: key, category: cat, context });
    }
    const val = node[key];
    if (typeof val === 'string') {
      if (classifyValue(val) === SENSITIVE_CATEGORY.TOKEN) throw new SensitiveDataViolation({ field: key, category: SENSITIVE_CATEGORY.TOKEN, context });
    } else if (Array.isArray(val) || isPlainObject(val)) {
      walkTokenSecret(val, context, depth + 1);
    }
  }
}

// ── Log / error redaction ─────────────────────────────────────────────────────
function redactString(s, maxLen) {
  let out = String(s)
    .replace(URL_QUERY, '$1?[redacted-query]')
    .replace(new RegExp(JWT_VALUE, 'g'), '[redacted-token]')
    .replace(new RegExp(BEARER_VALUE, 'gi'), '[redacted-token]')
    .replace(new RegExp(OAUTH_TOKEN_KV, 'gi'), '$1=[redacted-token]')
    .replace(SENSITIVE_KV, '$1=[redacted-secret]')
    .replace(new RegExp(EMAIL_VALUE, 'g'), '[redacted-email]');
  for (const re of PHONE_VALUES) out = out.replace(new RegExp(re, 'g'), '[redacted-phone]');
  out = out.replace(new RegExp(LONG_NUMBER_VALUE, 'g'), '[redacted-number]');
  if (typeof maxLen === 'number' && out.length > maxLen) out = out.slice(0, maxLen);
  return out;
}

export function redactSensitiveForLog(value, options) {
  const opts = options || {};
  const maxLen = typeof opts.maxStringLength === 'number' ? opts.maxStringLength : 500;
  return redactNode(value, maxLen, 0);
}

function redactNode(node, maxLen, depth) {
  if (depth > 8) return '[redacted-depth]';
  if (typeof node === 'string') return redactString(node, maxLen);
  if (node == null || typeof node === 'number' || typeof node === 'boolean') return node;
  if (Array.isArray(node)) return node.map((x) => redactNode(x, maxLen, depth + 1));
  if (isPlainObject(node)) {
    const out = {};
    for (const k of Object.keys(node)) {
      const cat = classifyFieldName(k);
      out[k] = cat ? `[redacted:${cat}]` : redactNode(node[k], maxLen, depth + 1);
    }
    return out;
  }
  return '[redacted]'; // non-plain object (FieldValue / class instance)
}

/** Centralized safe error projection for ops error docs / console. */
export function sanitizeErrorForOps(error, options) {
  const opts = options || {};
  const maxMessage = opts.maxMessage || 300;
  const maxStack = opts.maxStack || 200;
  const code = error && error.code != null ? String(error.code).slice(0, 120) : null;
  const rawMsg = error && error.message != null ? error.message : (error == null ? 'Unknown error' : error);
  const message = redactString(String(rawMsg), maxMessage);
  let stackTop = null;
  if (error && typeof error.stack === 'string' && error.stack.length) {
    stackTop = redactString(error.stack.split('\n')[0].trim(), maxStack);
  }
  return { code, message, stackTop };
}

// ── Storage path safety ───────────────────────────────────────────────────────
const CANONICAL_STORAGE_PATH = /^tenants\/[A-Za-z0-9_-]+\/(referrals|preReferralPackets)\/[A-Za-z0-9_-]+\/(attachments|documents)\/[A-Za-z0-9_-]{1,80}$/;

export function isSafeStoragePath(path) {
  return CANONICAL_STORAGE_PATH.test(String(path == null ? '' : path));
}

export function assertSafeStoragePath(path, context) {
  if (!isSafeStoragePath(path)) {
    throw new SensitiveDataViolation({ field: '(storagePath)', category: SENSITIVE_CATEGORY.ATTACHMENT_METADATA, context: context || 'storage object path' });
  }
  return true;
}

export default {
  SENSITIVE_CATEGORY,
  VIOLATION_CODE,
  FORBIDDEN_FIELD_CATEGORY,
  FORBIDDEN_NEVER_PHI_FIELDS,
  SAFE_OPERATIONAL_FIELDS,
  ACCESS_LOG_ALLOWED_REFERENCE_IDS,
  SensitiveDataViolation,
  normName,
  classifyValue,
  classifyFieldName,
  isForbiddenNeverPhiFieldName,
  assertSafeNeverPhiDoc,
  assertSafeAccessLogExtra,
  assertNoTokenOrSecret,
  redactSensitiveForLog,
  sanitizeErrorForOps,
  isSafeStoragePath,
  assertSafeStoragePath,
};
